import React, { useEffect, useState } from "react";
import { Box, Flex, Grid } from "@chakra-ui/react";
import { useAnimate, useInView } from "framer-motion";
import * as Sentry from "@sentry/nextjs";
import { DecorativeBackgroundImage } from "components/images";
import bgImage from "../../../../../../public/assets/platform/campaign-intelligence/breakdowns-bg.png";
import { HeaderLabel, MediumLabel, AnimatedSourceBar, SourceBarWrapper, BaseSourceBar } from "./components";

function useBreakdownsAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Second row
                ["#paid_tiktok_source_bar", { opacity: 1 }, { duration: 0.2 }],
                ["#paid_snap_source_bar", { opacity: 1 }, { at: "-0.1", duration: 0.2 }],
                ["#paid_google_source_bar", { opacity: 1 }, { at: "-0.1", duration: 0.2 }],
                // 0.40s

                // Third row
                ["#organic_medium_label", { opacity: 1 }, { delay: 0.1, duration: 0.2 }],
                ["#organic_source_bar", { opacity: 1 }, { delay: 0.05, duration: 0.2 }],
                // 0.95s

                // Fourth row
                ["#social_medium_label", { opacity: 1 }, { delay: 0.1, duration: 0.2 }],
                ["#social_source_bar", { opacity: 1 }, { delay: 0.05, duration: 0.2 }],
                // 1.50s

                // Fifth row
                ["#affiliate_medium_label", { opacity: 1 }, { delay: 0.1, duration: 0.2 }],
                ["#affiliate_youtube_source_bar", { opacity: 1 }, { delay: 0.05, duration: 0.2 }],
                ["#affiliate_tiktok_source_bar", { opacity: 1 }, { at: "-0.1", duration: 0.2 }],
                // 2.15s
              ]
            : [
                // Reset
                [".animated-breakdowns-element", { opacity: 0 }, { duration: 0.01 }],
              ],
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function BreakdownsAnimation() {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useBreakdownsAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.5 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex
      ref={scope}
      align="center"
      bg="new.violet.950"
      boxSize="100%"
      pos="relative"
      py={{ base: "small", lg: "bigger" }}
      px={{ base: "smaller", md: "small", lg: "bigger" }}
    >
      <DecorativeBackgroundImage src={bgImage} />

      <Box
        pos="relative"
        maxW={{ base: "90%", lg: "96%", xl: "580px" }}
        maxH={{ base: "auto", lg: "94%" }}
        mx="auto"
        mt={{ base: "auto", lg: "0" }}
      >
        <Grid
          templateColumns={{ base: "64px auto", md: "72px auto", lg: "115px auto" }}
          templateRows="repeat(6, min-content)"
          rowGap="6px"
          bg="white"
          bgGradient="linear(to-t, rgba(16, 17, 17, 0.02) 0%, rgba(16, 17, 17, 0.00) 100%)"
          borderRadius="smaller"
          boxShadow="md"
          flex={1}
          my="auto"
          w="100%"
          px={{ base: "small", lg: "big" }}
          py={{ base: "smaller", lg: "small" }}
          color="brand.textSecondary.light"
          fontSize="10px"
        >
          <HeaderLabel text="Medium" count={5} />
          <HeaderLabel text="Source" count={9} pl="4" />

          <MediumLabel id="email_medium_label">Email</MediumLabel>
          <SourceBarWrapper>
            <BaseSourceBar text="Email" color="new.violet.500" count={2094} />
          </SourceBarWrapper>

          <MediumLabel id="paid_medium_label">Paid</MediumLabel>
          <SourceBarWrapper py="6px">
            <BaseSourceBar id="paid_facebook_source_bar" text="Facebook" color="new.green.500" count={698} />
            <AnimatedSourceBar
              id="paid_tiktok_source_bar"
              text="TikTok"
              color="new.yellow.500"
              count={403}
              isInView={isInView}
              animationDelay={0.1}
            />
            <AnimatedSourceBar
              id="paid_snap_source_bar"
              text="Snap"
              color="new.blue.500"
              count={318}
              isInView={isInView}
              animationDelay={0.2}
            />
            <AnimatedSourceBar
              id="paid_google_source_bar"
              text="Google"
              color="new.violet.500"
              count={312}
              isInView={isInView}
              animationDelay={0.3}
            />
          </SourceBarWrapper>

          <MediumLabel isAnimated id="organic_medium_label">
            Organic
          </MediumLabel>
          <SourceBarWrapper>
            <AnimatedSourceBar
              id="organic_source_bar"
              text="Organic"
              color="new.green.500"
              count={1156}
              isInView={isInView}
              animationDelay={0.75}
            />
          </SourceBarWrapper>

          <MediumLabel isAnimated id="social_medium_label">
            Social
          </MediumLabel>
          <SourceBarWrapper>
            <AnimatedSourceBar
              id="social_source_bar"
              text="Social"
              color="new.yellow.500"
              count={375}
              isInView={isInView}
              animationDelay={1.3}
            />
          </SourceBarWrapper>

          <MediumLabel isAnimated id="affiliate_medium_label">
            Affiliate
          </MediumLabel>
          <SourceBarWrapper py="6px">
            <AnimatedSourceBar
              id="affiliate_youtube_source_bar"
              text="YouTube"
              color="new.blue.500"
              count={40}
              isInView={isInView}
              animationDelay={1.85}
            />
            <AnimatedSourceBar
              id="affiliate_tiktok_source_bar"
              text="TikTok"
              color="new.violet.500"
              count={15}
              isInView={isInView}
              animationDelay={1.95}
            />
          </SourceBarWrapper>
        </Grid>
      </Box>
    </Flex>
  );
}
